import * as THREE from 'three';

export class Maths {
	/**
	 * Rounds a float to the nearest 0.25
	 * */
	static roundQuarter(number: number) {
		return Math.round(number * 4) / 4;
	}

	/**
	 * Adds a random range to either side of a number
	 */
	static addBipolarRandom(original: number, randomRange: number) {
		const randomFactor = Math.random() * 2 - 1;
		return original + randomFactor * randomRange;
	}

	/**
	 * Generates numberOfItems positions in a grid of size range x range
	 */
	static generateRandomPositions(numberOfItems: number, range: number, minimumDistance: number = 1) {
		if (range === 0) throw new Error("Range cannot be 0.");

		const positions = [];

		// Helper function to calculate distance between two points
		function distance(p1: THREE.Vector3, p2: THREE.Vector3) {
			const dx = p2.x - p1.x;
			const dz = p2.z - p1.z;
			return Math.sqrt(dx * dx + dz * dz);
		}

		// Generate random positions until we have 20 positions
		const maxTries = 100;
		let currentTry = 0;
		while (positions.length < numberOfItems && currentTry < maxTries) {
			currentTry += 1;

			let newPosition: THREE.Vector3;
			if (range) newPosition = new THREE.Vector3(Math.random() * range, 0, Math.random() * range);
			else newPosition = new THREE.Vector3(0, 0, 0);

			let isValid = true;

			// Check distance from new position to existing positions
			for (const existingPosition of positions) {
				if (distance(newPosition, existingPosition) < minimumDistance) {
					isValid = false;
					break;
				}
			}

			// If position is valid, add it to the array
			if (isValid) {
				positions.push(newPosition);
			} else if (currentTry == maxTries) {
				positions.push(newPosition);
			}
		}

		if (positions.length < numberOfItems) throw new Error("Could not generate enough positions.");

		return positions;
	}

	/**
	 * Extend a line in 3D space
	 */
	extendLine3D(p1: THREE.Vector3, p2: THREE.Vector3, extensionDistance: number): THREE.Vector3 {
		const x1 = p1.x;
		const y1 = p1.y;
		const z1 = p1.z;
		const x2 = p2.x;
		const y2 = p2.y;
		const z2 = p2.z;

		// Calculate the direction vector
		const direction = [x2 - x1, y2 - y1, z2 - z1];

		// Calculate the length of the direction vector
		const length = Math.sqrt(direction[0] ** 2 + direction[1] ** 2 + direction[2] ** 2);

		// Normalize the direction vector
		const unitDirection = [
			direction[0] / length,
			direction[1] / length,
			direction[2] / length
		];

		// Calculate the extended point
		const extendedPoint = new THREE.Vector3(
			x2 + unitDirection[0] * extensionDistance,
			y2 + unitDirection[1] * extensionDistance,
			z2 + unitDirection[2] * extensionDistance
		);

		return extendedPoint;
	}

	/**
	 * Calculates the distance between two points
	 * @param point1
	 * @param point2 
	 * @returns number
	 */
	distanceBetweenPoints(point1: THREE.Vector3, point2: THREE.Vector3) {
		const dx = point2.x - point1.x;
		const dy = point2.y - point1.y;
		const dz = point2.z - point1.z;
	
		return Math.sqrt(dx * dx + dy * dy + dz * dz);
	}

	/**
	 * Eases both in, and yes you guessed it, out.
	 * @param t Time
	 * @returns 
	 */
	static easeInOutQuad(t: number) {
		return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
	}
}
